import type { ICellRendererComp, ICellRendererParams, ColDef } from "ag-grid-enterprise";

interface MultilineTextCellRendererParams extends ICellRendererParams {
  // When set and `params.data[urlField]` is present, the LAST rendered line is wrapped
  // in an anchor tag instead of plain text. Absent (existing consumers): unchanged.
  urlField?: string;
  // 0-based line indexes that get the decorative `accent-line` class (styled look, no
  // real href). Ignored outside link mode (no `urlField`).
  accentLineIndexes?: number[];
  // Submit a hidden same-origin POST form on click instead of a plain GET `<a href>` —
  // mirrors PostLinkCellRenderer. Default false: plain GET link, unchanged.
  postLink?: boolean;
  // 0-based index of the line that becomes the link. Defaults to the last line.
  linkLineIndex?: number;
  // Row-data field holding a display-only label rendered before the first line's text
  // (and outside its link). Blank/absent → the lines render alone.
  prefixField?: string;
  // CSS class for that prefix label.
  prefixClass?: string;
}

// Each line is its own block-level element (not `<br>`-joined text nodes) so per-line
// styling (accent class, real link) is possible.
export class MultilineTextCellRenderer implements ICellRendererComp {
  private eGui!: HTMLElement;

  init(params: MultilineTextCellRendererParams): void {
    // A block element, not inline — otherwise AG-Grid's tall single-line ancestor still
    // governs each line's height via the CSS "strut", regardless of our own line-height.
    this.eGui = document.createElement("div");
    this.eGui.style.lineHeight = "normal";

    const field = (params.colDef as ColDef)?.field ?? "";
    const data = params.data as Record<string, unknown>;
    const multiline = field ? (data?.[`${field}_multiline`] as string | undefined) : undefined;
    const value = multiline ?? (params.value as string | null | undefined) ?? "";
    const url = params.urlField ? (data?.[params.urlField] as string | undefined | null) : undefined;
    const accentLineIndexes = params.accentLineIndexes ?? [];
    const postLink = params.postLink ?? false;

    const linkMode = params.urlField !== undefined;
    const lines = value.split("\n");

    // Never part of `params.value`, so sort/filter/copy still see the bare lines.
    const prefixLabel = params.prefixField ? (data?.[params.prefixField] as string | undefined) : undefined;

    if (!linkMode) {
      lines.forEach((line, index) => {
        if (index > 0) this.eGui.appendChild(document.createElement("br"));
        if (index === 0 && prefixLabel) {
          this.eGui.appendChild(this.buildPrefix(prefixLabel, params.prefixClass));
        }
        this.eGui.appendChild(document.createTextNode(line));
      });
      return;
    }

    const linkLineIndex = params.linkLineIndex ?? lines.length - 1;

    lines.forEach((line, index) => {
      const isRealLink = index === linkLineIndex && !!url;
      const prefix = index === 0 && prefixLabel ? this.buildPrefix(prefixLabel, params.prefixClass) : undefined;

      if (isRealLink && postLink) {
        const lineEl = this.buildPostLinkLine(line, url as string);
        // Into the block wrapper ahead of the anchor, so the prefix isn't itself clickable.
        if (prefix) lineEl.insertBefore(prefix, lineEl.firstChild);
        this.eGui.appendChild(lineEl);
        return;
      }

      const lineEl = document.createElement(isRealLink ? "a" : "span");
      lineEl.textContent = line;
      lineEl.style.display = "block";
      if (isRealLink) {
        (lineEl as HTMLAnchorElement).href = url as string;
        lineEl.setAttribute("draggable", "false");
        lineEl.style.color = "inherit";
        lineEl.style.textDecoration = "none";
      } else if (accentLineIndexes.includes(index)) {
        lineEl.classList.add("multiline-text-cell-renderer__accent-line");
      }

      if (!prefix) {
        this.eGui.appendChild(lineEl);
        return;
      }

      // An anchor gets a block wrapper so the prefix sits beside it rather than inside
      // the clickable area; a plain span can host the prefix directly.
      if (isRealLink) {
        const wrapper = document.createElement("span");
        wrapper.style.display = "block";
        lineEl.style.display = "inline";
        wrapper.appendChild(prefix);
        wrapper.appendChild(lineEl);
        this.eGui.appendChild(wrapper);
      } else {
        lineEl.insertBefore(prefix, lineEl.firstChild);
        this.eGui.appendChild(lineEl);
      }
    });
  }

  // Trailing space inside the span keeps the gap without relying on the class's margin.
  private buildPrefix(label: string, className?: string): HTMLElement {
    const prefixEl = document.createElement("span");
    prefixEl.textContent = `${label} `;
    if (className) prefixEl.className = className;
    return prefixEl;
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  refresh(): boolean {
    return false;
  }

  // Same hidden-form-submit mechanism as PostLinkCellRenderer — a real browser POST
  // navigation, not fetch/AJAX, so a server-side redirect actually navigates the browser.
  private buildPostLinkLine(text: string, url: string): HTMLElement {
    const line = document.createElement("span");
    line.style.display = "block";

    const link = document.createElement("a");
    link.textContent = text;
    link.setAttribute("draggable", "false");
    link.style.color = "inherit";
    link.style.textDecoration = "none";
    link.href = url;

    const form = document.createElement("form");
    form.method = "post";
    form.action = url;
    form.style.display = "none";

    const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";
    const csrfInput = document.createElement("input");
    csrfInput.type = "hidden";
    csrfInput.name = "authenticity_token";
    csrfInput.value = csrfToken;
    form.appendChild(csrfInput);

    link.addEventListener("click", (e) => {
      e.preventDefault();
      form.submit();
    });

    line.appendChild(link);
    line.appendChild(form);
    return line;
  }
}
